/**
 * Collectr price application helpers.
 *
 * Two columns on user_items get populated by ImportCollectrDialog:
 *   - collectr_price_override_cents — what you set in Collectr as your asking price
 *   - collectr_market_price_cents   — Collectr's live market value at import time
 *
 * The "Use Collectr price" action prefers the override, falls back to market.
 * It only touches `asking_price_cents` — visibility, offers, cost basis stay put.
 */

import { supabase } from '@/integrations/supabase/client';
import { formatUsdCents } from '@/lib/formatMoney';

export interface CollectrPriceable {
  asking_price_cents?: number | null;
  collectr_market_price_cents?: number | null;
  collectr_price_override_cents?: number | null;
}

/**
 * Resolve the price we'd apply for this item.
 * Returns null if there's no Collectr-derived price available.
 */
export function resolveCollectrPriceCents(item: CollectrPriceable): number | null {
  const override = item.collectr_price_override_cents;
  if (typeof override === 'number' && override > 0) return override;
  const market = item.collectr_market_price_cents;
  if (typeof market === 'number' && market > 0) return market;
  return null;
}

/** True if applying would actually change asking_price. */
export function hasPendingCollectrPrice(item: CollectrPriceable): boolean {
  const resolved = resolveCollectrPriceCents(item);
  if (resolved === null) return false;
  return (item.asking_price_cents ?? null) !== resolved;
}

export function formatCents(cents: number): string {
  return `${formatUsdCents(cents)}`;
}

/** Apply Collectr price to a single item. Returns the cents value applied, or null if no-op. */
export async function applyCollectrPrice(itemId: string, item: CollectrPriceable): Promise<number | null> {
  const resolved = resolveCollectrPriceCents(item);
  if (resolved === null) return null;
  const { error } = await supabase
    .from('user_items')
    .update({ asking_price_cents: resolved })
    .eq('id', itemId);
  if (error) throw error;
  return resolved;
}

export interface BulkApplyResult {
  applied: number;
  skipped: number;
}

/**
 * Apply Collectr price to many items.
 * Items without a Collectr price are silently skipped (reported in the count).
 */
export async function applyCollectrPriceBulk(
  items: Array<{ id: string } & CollectrPriceable>,
): Promise<BulkApplyResult> {
  const eligible = items
    .map((it) => ({ id: it.id, price: resolveCollectrPriceCents(it) }))
    .filter((row): row is { id: string; price: number } => row.price !== null);

  if (eligible.length === 0) {
    return { applied: 0, skipped: items.length };
  }

  // Group by price so we can batch one UPDATE per distinct price value.
  const byPrice = new Map<number, string[]>();
  for (const row of eligible) {
    const bucket = byPrice.get(row.price) ?? [];
    bucket.push(row.id);
    byPrice.set(row.price, bucket);
  }

  for (const [price, ids] of byPrice) {
    const { error } = await supabase
      .from('user_items')
      .update({ asking_price_cents: price })
      .in('id', ids);
    if (error) throw error;
  }

  return { applied: eligible.length, skipped: items.length - eligible.length };
}
